import { GrundartikelLink } from '@/components/catalog/GrundartikelLink'
import type { ReactNode } from 'react'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import {
  AMAZON_LIVE_GATES_HINT,
  type FamilyStart, type FamilyVariante, type RegelInfo, type ScopeFolge, type ScopeVorschau, type ScopeWirkung,
} from '@/api/priceRollout'
import { fmtEUR } from '@/lib/pricing-format'
import { regelKlartext, schreibzielText } from '@/lib/rollout-klartext'
import { NaechsterSchritt } from '@/components/rollout/RolloutAnzeige'
import { cn } from '@/lib/utils'
import { grundText, portalName } from './rolloutView'
import { ROLLOUT_STATUS } from './PortalZelle'

const STRATEGIE_NAME = { margin: 'Marge', break_even: 'Break-Even' } as const

export const FOLGE: Record<ScopeFolge, string> = {
  neu: 'wird neu angelegt',
  ziel_aendern: 'Ziel wird geändert',
  unveraendert_dead_band: 'Ziel bleibt (Änderung zu klein)',
  reaktiviert: 'wird reaktiviert',
}

const WIRKUNG_HINWEIS: Partial<Record<ScopeWirkung, string>> = {
  keine_regel: 'keine Regel – der Tageslauf macht hier keinen Schritt',
  regel_aus: 'Regel ausgeschaltet – kein Schritt',
  gesperrt: 'LIVE derzeit gesperrt (Freigabe fehlt) – kein Schritt',
}

/** LIVE nach Regel — so wertet auch das Backend `alle_dry_run`. */
const istLive = (r: RegelInfo | null) => r !== null && r.modus === 'live'

function ModusChip({ regel }: { regel: RegelInfo | null }) {
  if (!regel) return <span className="text-ink-2">keine Regel</span>
  if (regel.modus === 'live') {
    return <span className="rounded bg-coral px-1.5 py-px text-[11px] font-bold tracking-wide text-ink">LIVE</span>
  }
  return <span className="rounded bg-panel-2 px-1.5 py-px text-[11px] text-ink">nur Protokoll</span>
}

function Feld({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wider text-muted">{label}</div>
      <div className="font-mono text-sm text-ink">{children}</div>
    </div>
  )
}

function ScopeKarte({ s, fanout, liveWrites, ist }: { s: ScopeVorschau; fanout: boolean; liveWrites: boolean; ist?: number | null }) {
  const senkt = ist !== undefined && ist !== null && s.ziel !== null && Math.round(s.ziel * 100) < Math.round(ist * 100)
  const live = istLive(s.regel)
  const hinweis = WIRKUNG_HINWEIS[s.wirkung]
    ?? (live && s.wirkung === 'dry_run' && !liveWrites ? 'globale LIVE-Freigabe aus – schreibt derzeit nur ins Protokoll' : null)
  return (
    <li
      data-testid={`scope-${s.portal}`}
      className={cn('rounded-md border border-line bg-panel px-3 py-2.5 border-l-4', live ? 'border-l-coral' : 'border-l-line')}
    >
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="font-display text-sm font-semibold text-ink">{portalName(s.portal)}</span>
        {fanout && <span className="rounded bg-panel-2 px-1.5 text-[10px] text-ink-2" title="DreamRobot gibt den Basispreis an dieses Portal weiter">DR leitet weiter</span>}
        <ModusChip regel={s.regel} />
        {s.regel && !s.regel.aktiv && <span className="text-[11px] text-ink-2">(Regel aus)</span>}
        {s.regel?.override && <span className="text-[11px] text-ink-2">Artikel-Override</span>}
        <span className="ml-auto text-xs font-medium text-ink">{FOLGE[s.folge]}</span>
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-4">
        <Feld label="Ziel alt → neu">
          {s.bestehend ? fmtEUR(s.bestehend.ziel) : '—'} → <b>{fmtEUR(s.ziel)}</b>
        </Feld>
        <Feld label="Mindestpreis">{fmtEUR(s.floor)}</Feld>
        <Feld label="Höchstpreis">{s.ceiling === null ? '—' : fmtEUR(s.ceiling)}</Feld>
        <Feld label="Bestehend">
          <span className="font-sans">{s.bestehend ? (ROLLOUT_STATUS[s.bestehend.status] ?? s.bestehend.status) : 'kein Rollout'}</span>
        </Feld>
      </div>
      {(s.angehoben || senkt) && (
        <div className="mt-1.5 flex flex-wrap gap-2 text-[11px]">
          {s.angehoben && <span className="rounded bg-panel-2 px-1.5 text-ink-2">auf Amazon-Gesamt angehoben</span>}
          {senkt && (
            <span className="rounded bg-warn-soft px-1.5 font-medium text-ink">
              senkt Preis {fmtEUR(s.ziel! - ist!).replace('-', '−')} (Ist {fmtEUR(ist!)})
            </span>
          )}
        </div>
      )}
      {s.folge === 'unveraendert_dead_band' && s.bestehend && (
        <p className="mt-1.5 text-[11px] text-ink-2">Änderung zu klein: das gespeicherte Ziel {fmtEUR(s.bestehend.ziel)} bleibt.</p>
      )}
      {(s.schreibweg !== undefined || s.nachher) && (
        <div data-testid={`scope-plan-${s.portal}`} className="mt-2 grid gap-x-4 gap-y-1.5 border-t border-line-2 pt-2 text-[11px] text-ink-2 sm:grid-cols-2">
          {s.schreibweg !== undefined && (
            <div><span className="text-muted">Schreibziel </span><b className="font-semibold text-ink">{schreibzielText(s.portal, s.schreibweg)}</b></div>
          )}
          {s.nachher && <NaechsterSchritt zeitplan={s.nachher.zeitplan} praefix="Erster Schritt" />}
          {s.nachher?.regel && <div className="sm:col-span-2">{regelKlartext(s.nachher.regel)}</div>}
        </div>
      )}
      {hinweis && <p className="mt-1.5 text-[11px] text-ink-2">{hinweis}</p>}
    </li>
  )
}

export function SammelZeile({ alleDryRun }: { alleDryRun: boolean }) {
  return (
    <div
      data-testid="sammelzeile"
      role="status"
      className={cn(
        'rounded-md border-l-4 px-3 py-2 text-sm text-ink',
        alleDryRun ? 'border-success bg-success-soft' : 'border-coral bg-coral-soft font-semibold',
      )}
    >
      {alleDryRun
        ? 'Nur Protokoll – dieser Start schreibt keinen Preis'
        : 'LIVE – dieser Start schreibt echte Preise, kein Teststart'}
    </div>
  )
}

interface StartProps {
  /** Aktuelle Familiendaten der gewählten Variante (null: Variante nicht mehr geliefert). */
  offen: { v: FamilyVariante; start: FamilyStart } | null
  wahlOffen: boolean
  liveWrites: boolean
  amazonGateOffen: boolean
  busy: boolean
  /** Grund, warum der Start gerade gesperrt ist (Neuladen, Ladefehler, Daten nach Änderung nicht frisch). */
  sperre: string | null
  /** Stand weicht vom beim Öffnen gezeigten ab: erst übernehmen, dann starten. */
  veraltet: boolean
  onUebernehmen: () => void
  fehler: string | null
  onAbbrechen: () => void
  onStarten: () => void
}

/** Bestätigung je betroffenem Scope (base + Fan-out bzw. Amazon). Alle Werte vom Backend. */
export function StartDialog({
  offen, wahlOffen, liveWrites, amazonGateOffen, busy, sperre, veraltet, onUebernehmen, fehler, onAbbrechen, onStarten,
}: StartProps) {
  const st = offen?.start
  const amazon = st?.scopes.some((s) => s.portal === 'amazon') ?? false
  return (
    <Dialog open={wahlOffen} onOpenChange={(o) => { if (!o && !busy) onAbbrechen() }}>
      <DialogContent className="sm:max-w-2xl">
        {wahlOffen && !offen && (
          <>
            <DialogHeader>
              <DialogTitle className="font-display">Rollout starten</DialogTitle>
              <DialogDescription>Diese Variante ist in den aktuellen Daten nicht mehr enthalten – kein Start möglich.</DialogDescription>
            </DialogHeader>
            <DialogFooter><Button variant="outline" onClick={onAbbrechen}>Schließen</Button></DialogFooter>
          </>
        )}
        {offen && st && (
          <>
            <DialogHeader>
              <DialogTitle className="font-display">
                {st.portal === 'amazon' ? 'Amazon' : 'Basis'}-Rollout starten ·{' '}
                <GrundartikelLink code={offen.v.kennung?.stern ?? offen.v.sku} className="font-mono" />
                {offen.v.kennung?.stern && <span className="ml-1.5 font-mono text-sm font-normal text-muted">{offen.v.sku}</span>}
              </DialogTitle>
              <DialogDescription>
                {st.fanout_portal === 'ebay'
                  ? 'Basis → eBay: zwei gekoppelte Rollouts mit einem Takt und gleichem Schritt.'
                  : st.fanout_portal
                    ? `Basispreis in DreamRobot – DR leitet weiter an ${portalName(st.fanout_portal)}.`
                    : 'Genau ein Rollout für diese Variante.'}{' '}
                Der Server prüft beim Start alles erneut.
              </DialogDescription>
            </DialogHeader>
            <ul className="space-y-2">
              {st.scopes.map((s, i) => (
                <ScopeKarte key={s.portal} s={s} fanout={i > 0} liveWrites={liveWrites} ist={i === 0 ? offen.v.spuren[st.portal].ist_preis : undefined} />
              ))}
            </ul>
            <SammelZeile alleDryRun={st.alle_dry_run} />
            {amazon && (
              <p className="rounded-md bg-warn-soft px-3 py-2 text-xs text-ink">
                Amazon LIVE schreibt direkt über die SP-API, nicht über DreamRobot.
                Amazon-Gebühren-Gate: {amazonGateOffen ? 'offen' : 'zu'}. {AMAZON_LIVE_GATES_HINT}
              </p>
            )}
            {veraltet && (
              <div role="alert" className="flex flex-wrap items-center gap-2 rounded-md border border-coral bg-coral-soft px-3 py-2 text-sm text-ink">
                <span className="flex-1">Die Daten haben sich seit dem Öffnen geändert (Ziel, Scopes oder Startbarkeit). Bitte oben neu prüfen.</span>
                <Button size="sm" variant="outline" onClick={onUebernehmen} disabled={sperre !== null || busy}>Neuen Stand übernehmen</Button>
              </div>
            )}
            {!st.startbar && (
              <p className="rounded-md bg-panel-2 px-3 py-2 text-sm text-ink-2">Start nicht möglich: {grundText(st) ?? 'gesperrt'}</p>
            )}
            {sperre && !busy && <p className="rounded-md bg-panel-2 px-3 py-2 text-sm text-ink-2">{sperre}</p>}
            {fehler && <p role="alert" className="rounded-md border border-coral bg-coral-soft px-3 py-2 text-sm text-ink">{fehler}</p>}
            <DialogFooter>
              <Button variant="outline" onClick={onAbbrechen} disabled={busy}>Abbrechen</Button>
              <Button onClick={onStarten} disabled={busy || sperre !== null || veraltet || !st.startbar || st.ziel === null}>
                Rollout starten
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

interface StrategieProps {
  offen: { v: FamilyVariante; neu: 'margin' | 'break_even'; ziel: 'amazon' | 'base' | 'both' } | null
  busy: boolean
  onAbbrechen: () => void
  onBestaetigen: () => void
}

// Basis trägt eBay, Shopify und Metro mit; Amazon hat eine eigene Strategie.
const SPUR_TEXT = { amazon: 'Amazon', base: 'Basis (eBay, Shopify, Metro)' } as const
const ZIEL_TEXT = {
  amazon: 'Nur Amazon wird umgestellt; Basis und eBay bleiben unverändert.',
  base: 'Nur die Basis wird umgestellt (eBay, Shopify und Metro folgen ihr); Amazon bleibt unverändert.',
  both: 'Amazon und Basis werden gemeinsam umgestellt.',
} as const

export function StrategieDialog({ offen, busy, onAbbrechen, onBestaetigen }: StrategieProps) {
  const v = offen?.v
  return (
    <Dialog open={offen !== null} onOpenChange={(o) => { if (!o && !busy) onAbbrechen() }}>
      <DialogContent>
        {offen && v && (
          <>
            <DialogHeader>
              <DialogTitle className="font-display">Strategie umstellen · <span className="font-mono">{v.sku}</span></DialogTitle>
              <DialogDescription>{ZIEL_TEXT[offen.ziel]}</DialogDescription>
            </DialogHeader>
            <ul className="space-y-1 font-mono text-sm text-ink">
              {(offen.ziel === 'both' ? (['amazon', 'base'] as const) : [offen.ziel]).map((spur) => (
                <li key={spur}>
                  {SPUR_TEXT[spur]}: {STRATEGIE_NAME[v.strategie[spur]]} → {STRATEGIE_NAME[offen.neu]}
                  {offen.neu === 'break_even' && v.spuren[spur].politik === 'exkl' && (
                    <span className="ml-2 font-sans text-xs text-ink-2">(zzgl. Versand: bleibt beim Margen-Preis)</span>
                  )}
                </li>
              ))}
            </ul>
            <p className="text-xs text-ink-2">
              Der Zielpreis wird danach vom Server neu berechnet. Laufende Rollouts behalten ihr gespeichertes Ziel bis zum nächsten Start.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={onAbbrechen} disabled={busy}>Abbrechen</Button>
              <Button onClick={onBestaetigen} disabled={busy}>Strategie umstellen</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

interface FortsetzenProps {
  offen: { sku: string; portal: string; id: number; ziel: number; regel: RegelInfo | null } | null
  busy: boolean
  fehler: string | null
  onAbbrechen: () => void
  onBestaetigen: () => void
}

export function FortsetzenDialog({ offen, busy, fehler, onAbbrechen, onBestaetigen }: FortsetzenProps) {
  return (
    <Dialog open={offen !== null} onOpenChange={(o) => { if (!o && !busy) onAbbrechen() }}>
      <DialogContent>
        {offen && (
          <>
            <DialogHeader>
              <DialogTitle className="font-display">
                {portalName(offen.portal)}-Rollout fortsetzen · <span className="font-mono">{offen.sku}</span>
              </DialogTitle>
              <DialogDescription>Der Rollout läuft mit seinem gespeicherten Ziel weiter.</DialogDescription>
            </DialogHeader>
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <span className="font-mono">Ziel {fmtEUR(offen.ziel)}</span>
              <ModusChip regel={offen.regel} />
            </div>
            {istLive(offen.regel) && (
              <p className="rounded-md border-l-4 border-coral bg-coral-soft px-3 py-2 text-sm font-semibold text-ink">LIVE – echte Preisschritte</p>
            )}
            {fehler && <p role="alert" className="rounded-md border border-coral bg-coral-soft px-3 py-2 text-sm text-ink">{fehler}</p>}
            <DialogFooter>
              <Button variant="outline" onClick={onAbbrechen} disabled={busy}>Abbrechen</Button>
              <Button onClick={onBestaetigen} disabled={busy}>Fortsetzen</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
